import React, { useEffect, useState } from "react";
import { useSpring, a, config } from "react-spring";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { isMobile } from "react-device-detect";
import classes from "./searchbar.module.scss";
import QrCodeReader from "../../QrCodeReader/QrReader.component";
import RoundedIcon from "../../../elements/buttons/roundedIcon.component.jsx";
import { MODULES, POIS } from "../../../../reduxStore/actions/main";
import StartDestination from "../../../../functions/classes/startDestination.class.js";
import { withRouter } from "react-router-dom";
import Toast from "../../../../functions/classes/toast.class.js";
import CampusCollection from "../../../../functions/classes/campus.class";
import {
  AppOverlay,
  AppOverlaySearch,
} from "../../../../functions/classes/miscelleanous.classes.js";
import { CUSTOMIZABLE_VALUES } from "../../mapbox/variables/CUSTOMIZABLE_VALUES.js";
import { statisticLogger } from "../../../statisticLogger/StatisticLogger.container";

/**
 * Das ist die Searchbar, die Oberhalb des Bildschirms angezeigt wird
 * @returns {JSX.Element}
 * @constructor
 */
function SearchBar(props) {
  const intl = useIntl();
  const [qrOpen, setQrOpen] = useState(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState("");
  const barProps = useSpring({
    config: config.wobbly,
    from: { opacity: 0, transform: `translateY(-3rem)` },
    to: { opacity: 1, transform: `translateY(0)` },
  });

  const openMainNavi = () => {
    props.closeInfoCard();
    if ("close" === props.mainMenuStatus) {
      props.openMainNav();
    } else {
      props.closeMainNav();
    }
  };

  const emptySearch = () => {
    props.setCurrentFeature(null);
  };

  const onReadId = async (result) => {
    const qrCode = await result;
    let start = false;
    let destination = false;

    if (!!qrCode.startNodeId) {
      start = StartDestination.createStartDestination(
        qrCode.startNodeId,
        intl.formatMessage({ id: "gps.selectedPlace" })
      );
      props.setStart(start);
    }

    if (qrCode.destinationId) {
      // const feature = POIS.all.find((p) => p.id === +qrCode.destinationId);
      const feature = POIS.all_objects[+qrCode.destinationId];
      if (!feature) {
        props.addToast(
          new Toast(
            "QRCodePoiNotFound",
            intl.formatMessage({ id: "qrCode.notFound.poi" }),
            Toast.toastTypes.error,
            [],
            5000
          )
        );
        throw `POI with id:"${+qrCode.destinationId}" not found`;
      }
      if (feature.hasLocation()) {
        destination = new StartDestination({
          poiId: feature.id,
          nodeId: feature.nodeId,
          isSet: true,
          lng: feature.getFeatureCenter()[0],
          lat: feature.getFeatureCenter()[1],
          level: feature.level,
          indoors: feature.indoors,
          name: feature.name,
          campusId: feature.campusId,
          address: feature.address,
          type: feature.type,
        });
        props.setTarget(destination);
      } else {
        props.addToast(
          new Toast(
            "noLocationFound",
            intl.formatMessage({ id: "qrCode.noLocation" }),
            Toast.toastTypes.info
          )
        );
        props.setCurrentFeature(feature);
        props.setPreOpen();
        console.warn(
          `POI with id: ${+qrCode.destinationId} has no location to point to`
        );
      }
    } else {
      props.addToast(
        new Toast(
          "noDestinationId",
          intl.formatMessage({ id: "qrCode.noDestinationId" })
        ),
        Toast.toastTypes.error
      );
    }
    setQrOpen(false);
    // wenn der QR Code keinen Start findet dann rip
    if (!start && !destination) return;

    props.setFetchNewRoute(true);

    const { piKey } = props.match.params;

    window.saveLog({
      piKey,
      qrKey: qrCode.name,
    });

    const globalParams = new URLSearchParams(props.location.search);
    if (start) {
      globalParams.set("start", start.id);
    } else {
      globalParams.delete("start");
    }

    if (destination) {
      globalParams.set("destination", destination.id);
    } else {
      globalParams.delete("destination");
    }

    globalParams.set(
      "campusKeys",
      CampusCollection.getCampusKeys(start, destination)
    );
    props.history.push(`/map/route?/${globalParams.toString()}`);
  };

  const openSearch = () => {
    statisticLogger.addLog({
      action: {
        group: "Search",
        name: "Search opening",
        id: "openingSearch",
        type: "click",
        movement: "enter",
        interaction: "open",
      },
    });
    props.setCurrentAppOverlay(
      new AppOverlaySearch(isMobile, "", {
        onClose: () => props.setCurrentAppOverlay(new AppOverlay()),
        mustHaveLocation: false,
        onPOIClicked: (feature, searchText) => {
          props.setCurrentAppOverlay(new AppOverlay());
          props.setCurrentFeature(feature);
          statisticLogger.addLog({
            action: {
              group: "Search",
              movement: "leave",
              interaction: "select",
              id: "searchSelectPOI",
              name: "Search Select POI",
              type: "click",
              content: {
                globalPoiIds: [feature.globalPoiId],
                globalPoiId: feature.globalPoiId,
                globalName: feature.name,
                search: searchText,
                category: feature.category,
                campusId: feature.campusId,
              },
            },
          });
        },
      })
    );
  };
  useEffect(() => {
    let placeholder;
    if (props.currentChildrenFather) {
      placeholder = (
        <span>
          <FontAwesomeIcon
            icon={"map-marker-alt"}
            color={CUSTOMIZABLE_VALUES.childMarker}
            className={"mr-2"}
          />
          <div className="mr-2 d-inline">
            {intl.formatMessage({
              id: "searchbar.searchPlaceholder_children",
            })}
          </div>
          <div className="d-inline text-dark">
            {props.currentChildrenFather.name}
          </div>
        </span>
      );
    } else {
      placeholder = (
        <div
          aria-label={intl.formatMessage({
            id: "searchbar.screenreader.searchPlaceholder_init",
          })}
        >
          {intl.formatMessage({
            id: "searchbar.searchPlaceholder_init",
          })}
        </div>
      );
    }
    setSearchPlaceholder(placeholder);
  }, [props.currentChildrenFather]);

  const urlParams = new URLSearchParams(window.location.search);
  const isFramed = !!urlParams.get("isFramed");

  return (
    <>
      <a.div
        role={"search"}
        style={barProps}
        className={`${classes.container} p-2 searchBar searchBar__mapInfoPage pointer-event-on d-flex max-width-30`}
      >
        <RoundedIcon
          onClick={props.currentChildrenFather ? emptySearch : openMainNavi}
          divClass={"roundedIcon__searchbar--menu mr-1"}
          iconClass={"roundedIcon__searchbar--menu__icon"}
          icon={props.currentChildrenFather ? ["fal", "times"] : "bars"}
          ariaLabel={intl.formatMessage({ id: "menu.screenreader.menuButton" })}
        />
        <div
          onClick={openSearch}
          role={"button"}
          aria-label={intl.formatMessage({
            id: "searchbar.screenreader.searchPlaceholder_init",
          })}
          className={`${classes.searchText} w-100 text-overflow-ellipsis`}
        >
          {searchPlaceholder}
        </div>
        {isMobile && MODULES.hatQrCode && !isFramed && (
          <RoundedIcon
            onClick={() => setQrOpen(true)}
            icon={["fal", "qrcode"]}
            title={intl.formatMessage({ id: "search.buttonQRCode" })}
            divClass={"roundedIcon__searchbar--qrCode"}
            iconClass={"roundedIcon__searchbar--qrCode__icon"}
          />
        )}
      </a.div>
      {qrOpen ? (
        <QrCodeReader
          history={props.history}
          readId={onReadId}
          canceled={() => setQrOpen(false)}
        />
      ) : null}
    </>
  );
}

export default withRouter(SearchBar);
